import React from 'react';
import { AlertOutlined } from '@ant-design/icons';
import { Popover, Tag, Tooltip } from 'antd';
import { ShoppingCart } from '@material-ui/icons';
import moment from 'moment';

import { urlKey } from '../../../services/api/api-urls';
import {
  getColModelItem,
  getItemsObj,
  getRandomKey,
  nbsStr,
  renderFn
} from '../../../services/lib/utils/helpers';
import ItipsForOrderDetails from '../../../components/common/i-tips/order-details';
import ItipsForProduct from '../../../components/common/i-tips/product';
import IProdtipsForCustomer from '../../../components/common/i-tips/customerProd';
import CommonDatePickerFilter from '../../../components/common/others/common-date-picker-filter';

// ============================================================================
// 常量定义
// ============================================================================

export const colKey: any = {
  customerId: urlKey.Customer,
  employeeId: urlKey.Employee,
  totalPrice: 'totalPrice',
  deliveryName: 'deliveryName',
  deliveryAddress: 'deliveryAddress',
  postalCode: 'postalCode',
  comments: 'comments',
  requiredDate: 'requiredDate',
  orderDate: 'orderDate',
  deliveryDate: 'deliveryDate',
  deliveryAsap: 'deliveryAsap',
  paid: 'paid',
  deliveryCityId: urlKey.City,
  orderStatusId: urlKey.OrderStatus,
  deliveryMethodId: urlKey.DeliveryMethod,
  orderNo: 'orderNo',
};

const colInfos: any = {
  basicInfo: {
    title: 'Company Name',
    field: 'basicInfo',
    keywords: [colKey.customerId, colKey.employeeId, colKey.orderNo, colKey.comments]
  },
  deliveryInfo: {
    title: 'Delivery Method',
    field: 'deliveryInfo',
    keywords: [
      colKey.deliveryName,
      colKey.deliveryCityId,
      colKey.deliveryAddress,
      colKey.postalCode,
      colKey.deliveryMethodId,
      colKey.deliveryDate
    ]
  },
  items: {
    title: 'Items',
    field: 'items',
  },
  stage: {
    title: 'Stage',
    field: 'stage',
    keywords: [colKey.orderStatusId]
  }
};

// ============================================================================
// 辅助函数
// ============================================================================

const keyInfosArray: any = () => {
  return [
    {
      key: colKey.customerId,
      label: '',
      otherOptions: {
        required: true,
        type: 'select',
        isOverrideSelectionOptions: true,
        valueJoinArray: ['customerCode']
      }
    },
    {
      key: colKey.employeeId,
      label: 'Sales',
      otherOptions: {
        type: 'select',
        isOverrideSelectionOptions: true,
        valueJoinArray: ['firstName', 'lastName']
      }
    },
    {
      key: colKey.totalPrice,
      label: 'Price',
      otherOptions: { type: 'inputNumber' }
    },
    { key: colKey.deliveryName, label: 'Delivery Name' },
    { key: colKey.deliveryDate, label: 'Delivery Date' },
    { key: colKey.deliveryAddress, label: 'Delivery Address' },
    { key: colKey.postalCode, label: 'Postal Code' },
    {
      key: colKey.orderNo,
      label: 'Order No',
      otherOptions: { disabled: true }
    },
    {
      key: colKey.comments,
      label: 'Comments',
      otherOptions: { type: 'inputTextArea' }
    },
    {
      key: colKey.deliveryCityId,
      label: 'Delivery City',
      otherOptions: { type: 'select' }
    },
    {
      key: colKey.orderStatusId,
      label: 'Location',
      otherOptions: { type: 'select' }
    },
    {
      key: colKey.deliveryMethodId,
      label: '',
      otherOptions: { type: 'select' }
    }
  ];
};

/**
 * 格式化大数字为 K 表示
 */
const formatLongNumber = (num: number): string => {
  return num > 10000 ? (num / 1000).toFixed(1) + 'K' : num.toString();
};

/**
 * 根据发货状态确定行颜色
 *
 * 颜色规则：
 * - 红色: 全部送达 (按数量或按箱)
 * - 橘黄色: 部分送达
 * - 深绿色: 未送达，库存够
 * - 浅蓝色: 未送达，部分库存可用 (0 < Stock < Order Quantity)
 * - 黑色: 未送达，库存为0
 */

/**
 * 计算取整库存（向上取整到整箱数量）
 * 例如：库存1900个，每箱1000个 → 取整后2000个（2箱）
 */
const getRoundedStockQuantity = (stockQuantity: number, packagingQuantity: number): number => {
  if (!stockQuantity || stockQuantity <= 0) return 0;
  if (!packagingQuantity || packagingQuantity <= 0) return stockQuantity;
  const roundedBoxes = Math.ceil(stockQuantity / packagingQuantity);
  return roundedBoxes * packagingQuantity;
};

const getAwaitingDispatchRowColor = (row: any, NeedColor: boolean): string => {
  if (!NeedColor) return 'black';

  const packagingQuantity = row.product.baseProduct?.packagingType?.quantity || 1;
  const requiredQuantity = row.quantity * packagingQuantity;
  const availableStock = requiredQuantity - row.deliveredQuantity;
  const deliveredBoxes = row.deliveredBoxes || 0; // 已交付箱数
  const roundedStockQuantity = getRoundedStockQuantity(row.stockQuantity, packagingQuantity);

  // 全部送达 (按数量或按箱) - 红色
  if (row.deliveredQuantity >= requiredQuantity || deliveredBoxes >= row.quantity) {
    return 'red';
  }

  // 部分送达 - 橘黄色
  if (row.deliveredQuantity > 0 || deliveredBoxes > 0) {
    return '#FFA500';
  }

  // 未送达，判断库存状态（使用取整库存）
  // 库存够 - 深绿色
  if (roundedStockQuantity >= availableStock) {
    return 'green'; // 深绿色
  }

  // 库存有但不够 - 浅蓝色
  if (roundedStockQuantity > 0 && roundedStockQuantity < availableStock) {
    return '#0099ff'; // 浅蓝色 (Partial Stock Available)
  }

  // 库存为0 - 黑色
  return 'black';
};

// ============================================================================
// 过滤函数
// ============================================================================

/**
 * 产品名称/代码过滤器
 */
export const myfiler = (filterValue: any, rowData: any): boolean => {
  const filter = filterValue.replace(/-/g, '\u2011').trim().toUpperCase();

  for (let ele of rowData.orderProduct) {
    const prodName = ele.product?.productName.replace(/-/g, '\u2011').trim().toUpperCase();
    const productCode = ele.product?.productCode.replace(/-/g, '\u2011').trim().toUpperCase();

    if (prodName.indexOf(filter) >= 0 || productCode.indexOf(filter) >= 0) {
      return true;
    }
  }
    const address = rowData?.order?.deliveryAddress || rowData?.deliveryAddress || ''
    return address?.toLowerCase().includes(filterValue.toLowerCase())

  return false;
};

/**
 * 客户名称/代码过滤器
 */
export const customerfiler = (filterValue: any, rowData: any): boolean => {
  const filter = filterValue.replace(/-/g, '\u2011').trim().toUpperCase();
  const companyName = rowData.customer?.company.replace(/-/g, '\u2011').trim().toUpperCase();
  const companyCode = rowData.customer?.customerCode.replace(/-/g, '\u2011').trim().toUpperCase();

  return companyName.indexOf(filter) >= 0 || companyCode.indexOf(filter) >= 0;
};

/**
 * PO 号过滤器
 */
export const pofiler = (filterValue: any, rowData: any): boolean => {
  const filter = filterValue.trim().toUpperCase();

  return (
    rowData.custOrderNo?.toUpperCase().indexOf(filter) >= 0 ||
    rowData.PoNo?.toUpperCase().indexOf(filter) >= 0
  );
};

/**
 * Order No 过滤器（Order Details 列显示的是 Order No，过滤按 orderNo 匹配）
 */
export const orderNofiler = (filterValue: any, rowData: any): boolean => {
  const filter = filterValue.trim().toUpperCase();

  return rowData.orderNo?.toUpperCase().indexOf(filter) >= 0;
};

/**
 * Order Date / REQ Date 列的日期段过滤判断：
 * - 两端都未设置时不过滤；只填一端时按单边过滤（inclusive）
 * - 与显示保持一致：UTC 转本地后按天比较
 */
const isOrderDateInRange = (filterValue: any, dateValue: any): boolean => {
  // material-table 会把右上角全局搜索框的文本作为 filterValue 传入；
  // 本列只处理日期范围对象，纯文本交给其它列匹配（返回 false 即可）
  if (typeof filterValue !== 'object' || filterValue === null) return false;
  if (!filterValue || (!filterValue.startDate && !filterValue.endDate)) return true;
  if (!dateValue) return false;
  const localDate = moment.utc(dateValue).local().format('YYYY-MM-DD');
  const startOk = !filterValue.startDate || localDate >= filterValue.startDate;
  const endOk = !filterValue.endDate || localDate <= filterValue.endDate;
  return startOk && endOk;
};

/**
 * 给列配置中的 Order Date（orderDate1）/ REQ Date（requiredDate1）套上
 * 日期段（Start date ~ End date）过滤，Awaiting Dispatch 与 Order Management 共用
 */
export const applyOrderDateRangeFilter = (columns: any[]) =>
  columns.map((row: any) => {
    if (row.field !== 'orderDate1' && row.field !== 'requiredDate1') return row;
    return {
      ...row,
      filtering: true,
      filterComponent: (filterProps: any) => (
        <CommonDatePickerFilter
          columnDef={filterProps.columnDef}
          onFilterChanged={filterProps.onFilterChanged}
        />
      ),
      customFilterAndSearch: (filterValue: any, rowData: any) =>
        isOrderDateInRange(filterValue, rowData[row.field])
    };
  });

// SW12-The Container Cafe  ：The Container Cafe - 12oz(400ml) Single Wall Compostable Paper Cup
const truncateProductInfo = (
  code: string,
  name: string,
  maxLength: number = 72
): { code: string; name: string; isTruncated: boolean } => {
  const separator = ' / ';
  const totalLength = code.length + separator.length + name.length;

  // 总长度不超限，直接返回
  if (totalLength <= maxLength) {
    return { code, name, isTruncated: false };
  }

  // 计算 name 可用长度，最少为 0
  const availableForName = Math.max(maxLength - code.length - separator.length - 1, 0); // 1 for '…'

  let truncatedName = name;
  let isTruncated = false;

  if (availableForName <= 0) {
    // name 无法显示，直接为空
    truncatedName = '';
    isTruncated = true;
  } else {
    // 截断 name
    truncatedName = name.substring(0, availableForName) + '…';
    isTruncated = true;
  }

  return { code, name: truncatedName, isTruncated };
};


// ============================================================================
// 渲染函数
// ============================================================================

/**
 * 渲染订单产品信息
 */
export const renderOrderProduct = (
  rowData: any,
  isAwaitingDispatchPage?: boolean,
  dispatchDatials?: any,
  isInvoice?: boolean,
  isDispatchPage?: boolean
) => {
  const qstyle: React.CSSProperties = {
    width: isAwaitingDispatchPage ? '12ch' : isDispatchPage ? '20ch' : '10ch',
    display: 'flex',
    textAlign: 'left',
  };
  const isOrderMode = !isInvoice && !isAwaitingDispatchPage && !isDispatchPage;

  return (
    (rowData.orderProduct?.length && (
      <div>
        {/* 渲染产品列表 */}
        {rowData.orderProduct.map((row: any) => (
          <div
            key={getRandomKey()}
            style={{
              display: 'flex',
              color: getAwaitingDispatchRowColor(row,   !(isInvoice || isDispatchPage))
            }}
          >
            {/* 产品信息 */}
      {/* 产品信息 - 优化版：代码和名称分开显示，智能截断 */}
      {row.productId && (() => {
        const productCode = row.product?.productCode || '';
        const productName = row.product?.productName || '';
        const { code: truncatedCode, name: truncatedName, isTruncated } = truncateProductInfo(productCode, productName, 72);
        const fullText = `${productCode} / ${productName}`;
        
        return (
          <div style={{ width: '68ch', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* <Tooltip title={isTruncated ? `Full Code: ${productCode}` : productCode}> */}
              <Tag 
                color="processing" 
                style={{ 
                  margin: 0, 
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  fontWeight: 600,
                  minWidth: '80px',
                  textAlign: 'center',
                  // maxWidth: '150px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {productCode}
              </Tag>
            {/* </Tooltip> */}
            
            {/* <Tooltip title={isTruncated ? fullText : productName}> */}
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <ItipsForProduct
                  id={row.productId}
                  isNotCutting={true}
                  label={truncatedName}
                  showSupplierData={!isAwaitingDispatchPage && !isDispatchPage}
                />
              </div>
            {/* </Tooltip> */}
          </div>
        );
      })()}

            {/* 发票模式 */}
            {isInvoice ? (
              <div style={{ width: '32ch', flex: 1, display: 'flex' }}>
                <div style={{ width: '16ch',backgroundColor: '#ecfdf5', color: '#006400' ,borderRadius: '6px', border: '1px solid #d1fae5',fontWeight: 500}}>
                  Carton: {row.packageQty} × {row.product.baseProduct?.packagingType?.quantity}
                </div>
                <div style={{ width: '16ch', color: '#00008B' }}>
                  ${row.unitPrice.toFixed(2)}
                  {row.purchPrice && (
                    <span>
                      &nbsp;[<ShoppingCart style={{ fontSize: '12px' }} />${row.purchPrice.toFixed(2)}]
                    </span>
                  )}
                </div>
              </div>
            ) : (
              /* 普通模式 */
              <div style={qstyle}>
              <Tag color="blue" style={{ margin: 0, fontSize: '12px' }}>
                {row.quantity} × {row.product?.baseProduct?.packagingType?.quantity}
              </Tag>
                {dispatchDatials?.length &&
                  nbsStr(
                    '/ ' +
                      dispatchDatials.filter((item: any) => item.productId === row.productId)
                        .length
                  )}
                {dispatchDatials?.length &&
                  nbsStr(
                    ' - ' +
                      formatLongNumber(
                        dispatchDatials
                          .filter((item: any) => item.productId === row.productId)
                          .reduce(
                            (previousValue: any, currentValue: any) =>
                              previousValue + currentValue.quantity,
                            0
                          )
                      )
                  )}
              </div>
            )}

            {/* 待发货页面额外信息 */}
            {isAwaitingDispatchPage && (
              <div style={{ width: '28ch', display: 'flex' }}>
                {(() => {
                  // 计算整行颜色
                  const rowColor = getAwaitingDispatchRowColor(row, true);

                  // 计算库存状态颜色（使用取整库存）
                  const packagingQuantity = row.product.baseProduct?.packagingType?.quantity || 1;
                  const requiredQuantity = row.quantity * packagingQuantity;
                  const availableStock = requiredQuantity - row.deliveredBoxes * packagingQuantity;
                  const deliveredBoxes = row.deliveredBoxes || 0;
                  const roundedStockQuantity = getRoundedStockQuantity(row.stockQuantity, packagingQuantity);
                  // const roundedInDeliveryingQuantity = getRoundedStockQuantity(row.inDeliveryingQuantity || 0, packagingQuantity);

                  // 判断是否全部送达
                  const isFullyDelivered = row.deliveredQuantity >= requiredQuantity || deliveredBoxes >= row.quantity;
                  // 判断是否部分送达（有交付但未全部送达）
                  const isPartialDelivered = !isFullyDelivered && (row.deliveredQuantity > 0 || deliveredBoxes > 0);

                  // 计算按箱算的数量（不足一箱按一箱算）
                  const roundedStockBoxes = Math.ceil(roundedStockQuantity / packagingQuantity); // 库存箱数
                  const availableStockBoxes = Math.ceil(availableStock / packagingQuantity); // 还需交付的箱数

                  // S库存显示颜色：根据库存状态显示
                  let stockColor;
                  if (isFullyDelivered) {
                    // 完全送达时，库存颜色与整行颜色一致
                    stockColor = rowColor;
                  } else if (roundedStockQuantity === 0) {
                    stockColor = 'black'; // 库存为0 - 黑色
                  } else if (isPartialDelivered) {
                    // 部分送达时，显示剩余库存状态颜色
                    if (roundedStockBoxes >= availableStockBoxes) {
                      stockColor = 'green'; // 深绿色
                    } else {
                      stockColor = '#0099ff'; // 浅蓝色 (有库存但不够)
                    }
                  } else {
                    // 无送达时，S库存使用整体行颜色
                    stockColor = rowColor;
                  }

                  return (
                    <>
                      <div style={{ width: '14ch', color: stockColor }}>
                        <b>S:</b>&nbsp;{formatLongNumber(row.stockQuantity)}
                      </div>
                      <div style={{ width: '7ch', color: rowColor }}>
                        <b>H:</b>
                        {row.thisQuantity}
                      </div>
                      <div style={{ width: '7ch', color: rowColor }}>
                        &nbsp;<b>D:</b>
                        {row.deliveredQuantity}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        ))}

        {/* 渲染选项列表 */}
        {rowData.orderOption?.map((row: any) => (
          <div key={getRandomKey()}>
            {row.optionId && (
              <>
                <Tag color="blue" style={{ margin: 0, 
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  fontWeight: 600,}}>
                  {nbsStr(row.option?.optionName)}
                </Tag>
              </>
            )}
            <span>
              &nbsp;${row.price.toFixed(2)}&nbsp;&nbsp;
            </span>
          </div>
        ))}

        {/* 发票总价 */}
        {isInvoice && (
          <div style={{
            marginTop: '6px',
            padding: '5px 10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderLeft: '4px solid #059669',
            borderRadius: '6px',
          }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#065f46', letterSpacing: '0.3px' }}>
              Total Price:
            </span>
            <span style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
              <span style={{ fontSize: '16px', fontWeight: 700, color: '#065f46' }}>
                $ {rowData.totalPrice}
              </span>
              <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#047857' }}>
                ${rowData.priceInclgst} [inclGST]
              </span>
            </span>
          </div>
        )}
      </div>
    )) ||
    null
  );
};

// ============================================================================
// 列模型配置
// ============================================================================

/**
 * 销售订单管理列模型
 */
const SalesOrderManagementColumnModel = (type: string, deliveryMethodLookup?: any, brandLookup?: any, salesLookup?: any) => {
  // 发票列配置
  if (type === 'invoice') {
    return [
      {
        ...getColModelItem(colInfos.basicInfo, keyInfosArray),
        render: (rowData: any) => {
          const customer = rowData.customer || {}
          const companyName = customer.company || ''
          const tradingName = customer.customerCode || ''
          const brandName = customer.brand?.brandName || brandLookup?.[customer.brandId] || ''
          const salesName = salesLookup?.[customer.employeeId] || ''

          // No extra info => no tooltip
          if (!companyName && !brandName && !salesName) {
            return <div>{tradingName}</div>
          }

          const header = companyName || tradingName
          const infoRow = (label: string, value: React.ReactNode) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 2 }}>
              <span style={{ opacity: 0.6, minWidth: 88 }}>{label}</span>
              <span style={{ fontWeight: 500 }}>{value}</span>
            </div>
          )

          return (
            <Tooltip
              placement="top"
              title={
                <div style={{ fontSize: 12, maxWidth: 280 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-word' }}>
                    {header}
                  </div>
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.25)', margin: '4px 0 6px' }} />
                  {companyName && infoRow('Trading Name', tradingName || '-')}
                  {infoRow(
                    'Brand',
                    brandName
                      ? <Tag color="geekblue" style={{ margin: 0 }}>{brandName}</Tag>
                      : '-'
                  )}
                  {infoRow(
                    'Sales',
                    salesName
                      ? <Tag color="green" style={{ margin: 0 }}>{salesName}</Tag>
                      : '-'
                  )}
                </div>
              }
            >
              <div style={{ cursor: 'default' }}>{tradingName}</div>
            </Tooltip>
          )
        },
        customFilterAndSearch: (filterValue: any, rowData: any) =>
          customerfiler(filterValue, rowData)
      },
      {
        title: 'Sales Person',
        field: 'employeeName',
        render: (rowData: any) => rowData.employeeName
      },
      {
        ...getColModelItem(colInfos.deliveryInfo, keyInfosArray),
        render: (rowData: any) => renderFn(getItemsObj(keyInfosArray(), rowData.order), [colKey.deliveryMethodId])
        // render: (rowData: any) => rowData.order.deliveryMethod.deliveryMethodName
      },
      {
        title: colInfos.items.title,
        field: colInfos.items.field,
        sorting: false,
        filtering: true,
        editable: 'never',
        render: (rowData: any) => renderOrderProduct(rowData, false, false, true),
        customFilterAndSearch: (filterValue: any, rowData: any) =>
          myfiler(filterValue, rowData)
      },
      {
        title: 'Invoice Date',
        field: 'invoiceDate',
        render: (rowData: any) => moment.utc(rowData.invoiceDate).local().format('DD/MM/YYYY')
      },
      {
        title: 'Note to Account',
        field: 'accountNote',
        render: (rowData: any) => {
          // 根据后台 NoteType 值设置颜色和样式
          const getNoteStyle = (noteType: string | undefined | null | boolean) => {
            const normalizedType = String(noteType || '').toLowerCase().trim()

            if (normalizedType === 'special' || noteType === true) {
              return {
                color: '#ff4d4f',
                bgColor: 'rgba(255, 77, 79, 0.1)',
                borderColor: '#ff4d4f',
                fontWeight: 'bold'
              }
            }

            if (normalizedType === 'regular' || noteType === false) {
              return {
                color: '#555555',
                bgColor: 'transparent',
                borderColor: '#d9d9d9',
                fontWeight: 'normal'
              }
            }

            return {
              color: '#999999',
              bgColor: 'transparent',
              borderColor: 'transparent',
              fontWeight: 'normal'
            }
          }

          // Debug logging - 检查所有可能的字段名
          console.log('accountNote full row data:', rowData)
          console.log('accountNoteType check:', {
            'accountNoteType': rowData.accountNoteType,
            'accountNoteType (lower)': rowData.accountnotetype,
            'account_note_type': rowData.account_note_type,
            'accountNoteType (camel)': rowData.AccountNoteType,
          })

          // 尝试多种可能的字段名
          const noteTypeValue = rowData.order.accountNoteType || rowData.accountNoteType 
          const style = getNoteStyle(noteTypeValue)
          const hasNote = (rowData.order.accountNoteType||rowData.accountNote) && (rowData.order.accountNoteType ||rowData.accountNote.trim())

          if (!hasNote) {
            return <span style={{ color: '#999', fontSize: '14px' }}>-</span>
          }

          return (
            <Tooltip
              title={
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {rowData.accountNote}
                </div>
              }
            >
              <span
                style={{
                  fontSize: '14px',
                  color: style.color,
                  backgroundColor: style.bgColor,
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: style.borderColor !== 'transparent' ? `1px solid ${style.borderColor}` : 'none',
                  fontWeight: style.fontWeight,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  display: 'inline-block',
                  maxWidth: '220px',
                  lineHeight: '1.4'
                }}
              >
                {rowData.accountNote}
              </span>
            </Tooltip>
          )
        }
      },
      {
        title: 'PO No',
        field: 'PoNo',
        render: (rowData: any) => rowData.PoNo,
        customFilterAndSearch: (filterValue: any, rowData: any) => pofiler(filterValue, rowData)
      },
      {
        title: 'Invoice Number',
        field: 'invoiceNote',
        render: (rowData: any) => rowData.invoiceNote
      },
      {
        title: 'Packing Slip',
        field: 'packingSlipNo',
        render: (rowData: any) => rowData.packingSlipNo,
        customFilterAndSearch: (filterValue: any, rowData: any) => {
          let strPackingSlipNo = '';
          rowData.order.dispatching.map((e: any) => {
            strPackingSlipNo = strPackingSlipNo + ',' + e.packingSlipNo;
          });
          return strPackingSlipNo.includes(filterValue);
        }
      },
      {
        title: 'Profit',
        field: 'profit',
        render: (rowData: any) => '$' + rowData.profit
      },
      {
        title: 'Date of Payment',
        field: 'paymentDate',
        render: (rowData: any) =>
          rowData.paymentDate
            ? moment.utc(rowData.paymentDate).local().format('DD/MM/YYYY')
            : 'NA'
      },
      {
        title: 'Paid',
        field: colKey.paid,
        initialEditValue: 1,
        type: 'numeric',
        lookup: { 0: 'No', 1: 'Yes' }
      }
    ];
  }

  // 新发票列配置
  if (type === 'newInvoice') {
    return [
      {
        ...getColModelItem(colInfos.deliveryInfo, keyInfosArray),
        render: (rowData: any) => renderFn(getItemsObj(keyInfosArray(), rowData.order), [colKey.deliveryMethodId])
        // render: (rowData: any) => rowData.order.deliveryMethod.deliveryMethodName
      },
      {
        title: 'Product Description',
        field: 'productOrOptionName',
        width: 300,
        render: (rowData: any) => rowData.productOrOptionName
      },
      {
        title: 'Units per Pack',
        field: 'packageQty',
        render: (rowData: any) => rowData.packageQty
      },
      {
        title: 'Total Pack',
        field: 'numberQuantity',
        render: (rowData: any) => rowData.numberQuantity
      },
      {
        title: 'Total Units',
        field: 'totalQty',
        render: (rowData: any) => rowData.totalQty
      },
      {
        title: 'Price Per Pack',
        field: 'unitPrice',
        render: (rowData: any) => rowData.unitPrice != null ? '$' + rowData.unitPrice : '-'
      },
      {
        title: 'Total Amount',
        field: 'totalPrice',
        render: (rowData: any) => rowData.totalPrice != null ? '$' + rowData.totalPrice : '-'
      },
      {
        title: 'Profit Rate',
        field: 'profitRate',
        render: (rowData: any) => rowData.profitRate != null ? rowData.profitRate : '-'
      },
      {
        title: 'Reserve Price',
        field: 'reservePrice',
        render: (rowData: any) => rowData.reservePrice != null ? '$' + rowData.reservePrice : '-'
      },
      {
        title: 'Total',
        field: 'reserveTotalPrice',
        render: (rowData: any) => rowData.reserveTotalPrice != null ? '$' + rowData.reserveTotalPrice : '-'
      }
    ];
  }

  // 默认列配置
  return [
    {
      ...getColModelItem(colInfos.basicInfo, keyInfosArray),
        render: (rowData: any) => (
          <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            <IProdtipsForCustomer
              id={rowData.customer.customerId}
              label={rowData.customer.customerCode}
            />
          </div>
            )
          },
          {
            // ...getColModelItem(colInfos.deliveryInfo, keyInfosArray),
            title: 'Delivery Method',
            field: 'deliveryMethodId',
            filtering: true,
            ...(deliveryMethodLookup && Object.keys(deliveryMethodLookup).length > 0 ? { lookup: deliveryMethodLookup } : {}),
            render: (rowData: any) => (
          <Tag
            color="purple"
            style={{
              fontSize: '12px',
              whiteSpace: 'normal',
              wordBreak: 'keep-all',      // 不在单词中间断行
              overflowWrap: 'break-word'  // 如果太长的单词才会换行
            }}
          >
            {rowData.deliveryMethod?.deliveryMethodName}
          </Tag>
            )
          },
  //           {
  //   title: 'Delivery Method2',
  //   field: 'deliveryMethodId2',
  //   lookup: { 1: 'Pickup', 2: 'Standard', 3: 'Express' },
  //   filtering: true, // 启用过滤
  // },
          {
            title: colInfos.items.title,
            field: colInfos.items.field,
            sorting: false,
            editable: 'never',
            render: (rowData: any) => <div style={{ whiteSpace: 'normal' }}>{renderOrderProduct(rowData)}</div>,
            customFilterAndSearch: (filterValue: any, rowData: any) => myfiler(filterValue, rowData)
          },
          {
            title: 'Order Details',
            field: 'orderId',
            customFilterAndSearch: (filterValue: any, rowData: any) => orderNofiler(filterValue, rowData),
            render: (rowData: any) => (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {rowData.orderNo ?? '-'}
                </span>
                <ItipsForOrderDetails id={rowData.orderId} />
              </div>
            )
          },
          {
            title: 'Note',
            field: 'newWarehouseNote',
            render: (rowData: any) => {
              // 根据后台 warehouseNoteType 值设置颜色和样式
              const getWarehouseNoteStyle = (noteType: string | undefined | null | boolean) => {
                const normalizedType = String(noteType || '').toLowerCase().trim()

                if (normalizedType === 'special' || noteType === true) {
                  return {
                    color: '#ff4d4f',
                    bgColor: 'rgba(255, 77, 79, 0.1)',
                    borderColor: '#ff4d4f',
                    fontWeight: 'bold'
                  }
                }

                if (normalizedType === 'regular' || noteType === false) {
                  return {
                    color: '#555555',
                    bgColor: 'transparent',
                    borderColor: '#d9d9d9',
                    fontWeight: 'normal'
                  }
                }

                return {
                  color: '#999999',
                  bgColor: 'transparent',
                  borderColor: 'transparent',
                  fontWeight: 'normal'
                }
              }

              const style = getWarehouseNoteStyle(rowData.warehouseNoteType)
              const hasNote = rowData.newWarehouseNote && rowData.newWarehouseNote.trim()

              if (!hasNote) {
                return <span style={{ color: '#999', fontSize: '14px' }}>-</span>
              }

              return (
                <Tooltip
                  title={
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {rowData.newWarehouseNote}
                    </div>
                  }
                >
                  <span
                    style={{
                      fontSize: '14px',
                      color: style.color,
                      backgroundColor: style.bgColor,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: style.borderColor !== 'transparent' ? `1px solid ${style.borderColor}` : 'none',
                      fontWeight: style.fontWeight,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      display: 'inline-block',
                      maxWidth: '220px',
                      lineHeight: '1.4'
                    }}
                  >
                    {rowData.newWarehouseNote}
                  </span>
                </Tooltip>
              )
            }
          },
          {
            title: 'Order Date',
            field: 'orderDate1',
            type: 'datetime',
            editable: 'never',
            render: (rowData: any) => moment.utc(rowData.orderDate).local().format('DD/MM/YY'),
      customFilterAndSearch: (filterValue: any, rowData: any) => {
        if (
          filterValue &&
          (!filterValue.startDate ||
            !filterValue.endDate ||
            filterValue.startDate > filterValue.endDate)
        ) {
          return false;
        }
        return (
          !filterValue ||
          (rowData.createdAt >= filterValue.startDate &&
            rowData.createdAt.slice(0, 10) <= filterValue.endDate)
        );
      }
    },
    {
      title: 'REQ Date',
      field: 'requiredDate1',
      render: (rowData: any) => (
        <div>
          {rowData.deliveryAsap === 1 && (
            <div>
              <Popover
                content={rowData.urgentNote}
                title={<div style={{ textAlign: 'center' }}>Urgent</div>}
              >
                <AlertOutlined style={{ textAlign: 'center', fontSize: '18px', color: 'red' }} />
              </Popover>
            </div>
          )}
          <div>{moment.utc(rowData.requiredDate).local().format('DD/MM/YY')}</div>
        </div>
      )
    },
    {
      title: 'PO No',
      field: 'custOrderNo',
      render: (rowData: any) => {
        const value = rowData.custOrderNo || '-';
        const maxLength = 12; // 可根据需要调整最大显示长度
        const isLong = value.length > maxLength;
        const displayText = isLong ? value.slice(0, maxLength) + '…' : value;

        return (
          <Tooltip title={isLong ? value : ''}>
            <Tag color="orange" style={{ fontSize: '0.75rem' }}>{displayText}</Tag>
          </Tooltip>
        );
      },
      customFilterAndSearch: (filterValue: any, rowData: any) => pofiler(filterValue, rowData),
    }
    ,
    {
      title: 'Created By',
      field: 'CreatedEmployeeName'
    },
    {
      title: 'Updated By',
      field: 'operEmployeeName'
    }
    // ,
    // {
    //   title: 'Location',
    //   field: 'orderStatusId',
    //   sorting: false,
    //   lookup: {
    //     1: 'New',
    //     2: 'Online',
    //     3: 'Approved',
    //     10: 'Production',
    //     11: 'Warehouse',
    //     12: 'Dispatch',
    //     15: 'Delivered',
    //     16: 'Paid',
    //     20: 'Closed'
    //   },
    //   render: (rowData: any) => (
    //     <div>
    //       <div>{rowData[colKey.orderStatusId]?.[colKey.orderStatusId + 'Name']}</div>
    //       {type === 'awaiting' && <ItipsForOrderDetails id={rowData.orderId} />}
    //     </div>
    //   )
    // }
  ];
};

export default SalesOrderManagementColumnModel;
